import { useState, useEffect, useCallback } from 'react'
import { ContinentsService } from '../services/api/continentsService'
import { Continent } from '../types/continent'
import { logger } from '../utils/logger'

interface UseContinentsReturn {
  continents: Continent[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Global cache for continents
let continentsCache: Continent[] | null = null
let cachePromise: Promise<Continent[]> | null = null

export const useContinents = (): UseContinentsReturn => {
  const [continents, setContinents] = useState<Continent[]>(continentsCache || [])
  const [isLoading, setIsLoading] = useState(!continentsCache)
  const [error, setError] = useState<string | null>(null)

  const fetchContinents = useCallback(async (): Promise<Continent[]> => {
    // If we already have a pending request, return that promise
    if (cachePromise) {
      return cachePromise
    }

    // If we have cached data, return it
    if (continentsCache) {
      return continentsCache
    }

    // Create new request
    cachePromise = ContinentsService.getContinents()

    try {
      const data = await cachePromise
      continentsCache = data
      setContinents(data)
      setError(null)
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch continents'
      logger.error('Error fetching continents:', err)
      setError(errorMessage)
      throw err
    } finally {
      cachePromise = null
      setIsLoading(false)
    }
  }, [])

  const refetch = useCallback(async () => {
    // Clear cache to force refresh
    continentsCache = null
    cachePromise = null
    setIsLoading(true)
    await fetchContinents()
  }, [fetchContinents])

  useEffect(() => {
    if (!continentsCache && !cachePromise) {
      fetchContinents()
    } else if (continentsCache) {
      // Data already cached
      setContinents(continentsCache)
      setIsLoading(false)
    }
  }, [fetchContinents])

  return {
    continents,
    isLoading,
    error,
    refetch
  }
}